import numeral from 'numeral';
import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Input } from './styled';

export interface InputNumberProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'min' | 'max' | 'value'
  > {
  min?: number;
  max?: number;
  value?: number;
  formatter?: string;
  zeroReplacer?: string;
  onChange?(value: number, event: ChangeEvent<HTMLInputElement>);
  allowEmptyField?: boolean;
  disableDecimal?: boolean;
  disableHighlight?: boolean;
}

const InputNumber = forwardRef<HTMLInputElement, InputNumberProps>(
  (
    {
      className,
      onBlur = () => {},
      onChange = () => {},
      value: propValue,
      formatter = '0.[0000]',
      allowEmptyField,
      disableDecimal,
      defaultValue,
      zeroReplacer,
      ...props
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState<string>(
      defaultValue ? numeral(defaultValue).format(formatter) : '',
    );
    const numeralInputValue = useMemo(() => numeral(inputValue), [inputValue]);

    const setTextCursor = (
      target: EventTarget & HTMLInputElement,
      position: number,
    ) => {
      window.requestAnimationFrame(() => {
        target.focus();
        target.setSelectionRange(position, position);
      });
    };

    const getClampedValue = (value: number) =>
      Math.min(Math.max(value, props.min ?? -Infinity), props.max ?? Infinity);

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      onBlur(e);

      if (inputValue === '' && allowEmptyField) return;
      setInputValue(numeral(inputValue).format(formatter));
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const { value: eventValue, selectionStart } = e.target;

      if (
        zeroReplacer !== undefined &&
        (eventValue === '' || eventValue === '0')
      ) {
        setInputValue(zeroReplacer);
        onChange(0, e);
        return;
      }

      // Handle allow empty field when clear all
      if (eventValue === '' && allowEmptyField) {
        setInputValue(eventValue);
        onChange(undefined, e);
        return;
      }

      // Handle initial input when current value is 0, for replacing initial 0
      const currentValue = numeralInputValue.value() || 0;
      const numeralInstance = numeral(
        currentValue === 0 && (Number(eventValue) < 9 || selectionStart < 2)
          ? eventValue.replace('0', '')
          : eventValue,
      );

      // Handle for characters input resulting NaN
      if (Number.isNaN(numeralInstance.value())) return;

      // Handle initial decimal input
      if (
        eventValue[eventValue.length - 1] === '.' &&
        !inputValue.includes('.') &&
        !disableDecimal
      ) {
        setInputValue(`${numeralInstance.format(formatter)}.`);
        return;
      }

      // Prevent adding value more than maximum or minimum value
      numeralInstance.set(getClampedValue(numeralInstance.value()));
      const value = numeralInstance.value();
      if (eventValue === inputValue) {
        setTextCursor(e.target, selectionStart);
        return;
      }

      // Set final value
      setInputValue(numeralInstance.format(formatter));
      onChange(value, e);

      // Calculate shifting cursor with decimal and thousand separator
      const shiftPos =
        currentValue?.toString().length - value?.toString().length || 0;
      const newCursor =
        selectionStart +
        (numeralInstance.format(formatter).length -
          numeralInputValue.format(formatter).length +
          shiftPos);
      setTextCursor(e.target, newCursor <= 0 ? 1 : newCursor);
    };

    const handlePropValueChange = useCallback(() => {
      const isPropValueEmpty = propValue === undefined || propValue === null;

      if (isPropValueEmpty && allowEmptyField) return;

      const isReplaceZero = zeroReplacer !== undefined && propValue === 0;
      setInputValue(
        isReplaceZero
          ? zeroReplacer
          : numeral(getClampedValue(propValue)).format(formatter),
      );
    }, [propValue]);

    useEffect(() => {
      handlePropValueChange();
    }, [handlePropValueChange]);

    return (
      <Input
        ref={ref}
        className={`ant-input ${className}`}
        onChange={handleChange}
        onBlur={handleBlur}
        value={inputValue}
        {...props}
      />
    );
  },
);

export default InputNumber;
